import { prefersReducedMotion } from '@theme/utilities';

const SELECTOR = '[data-animate-in]';
const READY_CLASS = 'animate-in--ready';
const VISIBLE_CLASS = 'animate-in--visible';
const BOUND_ATTR = 'data-animate-in-bound';

/**
 * Scroll-triggered reveal animations.
 * Elements opt in with data-animate-in="<effect>".
 * Above-the-fold elements are shown immediately to protect LCP.
 */
class AnimateInController {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  constructor() {
    if (prefersReducedMotion()) {
      this.#revealAll(document);
      return;
    }

    this.#observer = new IntersectionObserver(this.#onIntersect, {
      threshold: 0.12,
      rootMargin: '0px 0px -8% 0px',
    });

    this.#scan(document);

    document.addEventListener('shopify:section:load', (event) => {
      const target = /** @type {Event & { target: Element }} */ (event).target;
      this.#scan(target);
    });
  }

  /**
   * @param {ParentNode} root
   */
  #scan(root) {
    const elements = root.querySelectorAll?.(SELECTOR) ?? [];
    for (const el of elements) {
      this.#setup(/** @type {HTMLElement} */ (el));
    }

    if (root instanceof Element && root.matches(SELECTOR)) {
      this.#setup(/** @type {HTMLElement} */ (root));
    }
  }

  /**
   * @param {ParentNode} root
   */
  #revealAll(root) {
    const elements = root.querySelectorAll?.(SELECTOR) ?? [];
    for (const el of elements) {
      el.classList.add(VISIBLE_CLASS);
    }
  }

  /**
   * @param {HTMLElement} el
   */
  #setup(el) {
    if (el.hasAttribute(BOUND_ATTR)) return;
    el.setAttribute(BOUND_ATTR, '');

    if (prefersReducedMotion() || this.#isInView(el)) {
      el.classList.add(VISIBLE_CLASS);
      return;
    }

    el.classList.add(READY_CLASS);
    this.#observer?.observe(el);
  }

  /**
   * @param {HTMLElement} el
   */
  #isInView(el) {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < vh * 0.92 && rect.bottom > 0;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const el = /** @type {HTMLElement} */ (entry.target);
      el.classList.add(VISIBLE_CLASS);
      this.#observer?.unobserve(el);
    }
  };
}

new AnimateInController();
